import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { Observable, BehaviorSubject } from 'rxjs';
import { User } from '../models/structure/base/user.model';
import { StorageService } from './storage.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private env = environment;
  private _isAuthenticated = new BehaviorSubject(false);
  public redirectUrl: string;

  get isAuthenticated(): Observable<boolean> {
    const token = this.readToken();

    if (token) {
      this._isAuthenticated.next(true);
    } else {
      this._isAuthenticated.next(false);
    }

    return this._isAuthenticated.asObservable();
  }

  constructor(private httpClient: HttpClient, private storageService: StorageService, private router: Router) {}

  login(email: string, password: string, navigateTo?: string): Observable<{ CurrentUser: User; Token: string }> {
    const body = {
      Email: email,
      Password: password
    };

    return new Observable((subscriber) => {
      this.httpClient.post(this.env.services.structure + '/base/user/login', body).subscribe(
        (response: { CurrentUser: User; Token: string }) => {
          if (response?.CurrentUser && response?.Token) {
            this.storageService.setItem('currentUser', response.CurrentUser, true);
            this.storageService.setItem('token', response.Token, true);
            this._isAuthenticated.next(true);

            if (navigateTo || this.redirectUrl) {
              this.router.navigate([this.redirectUrl ? this.redirectUrl : navigateTo], {queryParamsHandling: 'merge'});
              this.redirectUrl = '';
            }
          }
          subscriber.next(response);
          subscriber.complete();
        },
        (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      );
    });
  }

  setPassword(userId: string, input: { token: string; password: string }): Observable<User> {
    const body = {
      Token: input.token,
      Password: input.password
    };

    return new Observable((subscriber) => {
      this.httpClient.put(this.env.services.structure + '/base/user/set-password/' + userId, body).subscribe(
        (response: User) => {
          subscriber.next(response);
          subscriber.complete();
        },
        (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      );
    });
  }

  forgotPassword(email: string): Observable<any> {
    const body = {
      Email: email
    };

    return new Observable((subscriber) => {
      this.httpClient.put(this.env.services.structure + '/base/user/reset-password', body).subscribe(
        (response) => {
          subscriber.next(response);
          subscriber.complete();
        },
        (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      );
    });
  }

  logout() {
    this.storageService.removeItem('token');
    this.storageService.removeItem('currentUser');
    this._isAuthenticated.next(false);
    this.router.navigate(['/auth/login']);
  }

  readToken(): string | null {
    return this.storageService.getItem('token');
  }
}
